import AudioPlayer from '@components/audio/AudioPlayer';
import { formatDuration } from '@utils/misc/formatDuration';
import { utcDateTimeExpressedInLocalTimeZone } from '@utils/misc/functions';
import React, { CSSProperties } from 'react';
import { IAircallCall } from '@services/aircall/AircallService';

interface CallRowProps {
    call: IAircallCall;
    isExpanded: boolean;
    onToggle: () => void;
    fetchAudioUrl: () => Promise<string>;
}

const EMPTY = '—';

const directionBadge = (direction: string | null): React.ReactNode => {
    if (!direction) return <span>{EMPTY}</span>;

    const isInbound = direction === 'inbound';
    const style: CSSProperties = {
        backgroundColor: isInbound ? '#ECFDF5' : '#EFF6FF',
        borderRadius: '12px',
        color: isInbound ? '#065F46' : '#1E40B0',
        display: 'inline-block',
        fontSize: '12px',
        fontWeight: 600,
        padding: '2px 8px',
    };

    return <span style={style}>{isInbound ? '↙ In' : '↗ Out'}</span>;
};

const columnStyle = (width: string): CSSProperties => ({
    flexShrink: 0,
    width,
});

const CallRow = ({ call, isExpanded, onToggle, fetchAudioUrl }: CallRowProps) => {
    const rowStyle: CSSProperties = {
        borderBottom: '1px solid #C4C4C4',
        cursor: 'pointer',
        padding: '12px 16px',
    };

    const rowHeaderStyle: CSSProperties = {
        alignItems: 'center',
        display: 'flex',
    };

    const dateTimeStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
    };

    const dateStyle: CSSProperties = {
        fontWeight: 600,
        fontSize: '14px',
    };

    const timeStyle: CSSProperties = {
        color: '#6B7280',
        fontSize: '12px',
        marginTop: '2px',
    };

    const detailStyle: CSSProperties = {
        display: 'grid',
        gap: '8px 24px',
        gridTemplateColumns: '1fr 1fr',
        padding: '12px 16px 16px',
    };

    const detailItemStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    };

    const detailLabelStyle: CSSProperties = {
        color: '#6B7280',
        fontSize: '12px',
        fontWeight: 500,
    };

    const detailValueStyle: CSSProperties = {
        fontSize: '14px',
    };

    return (
        <div data-testid={`call-row-${call.call_uuid}`} style={rowStyle}>
            <div data-testid={`call-row-header-${call.call_uuid}`} onClick={onToggle} style={rowHeaderStyle}>
                <div style={{ ...columnStyle('12%'), ...dateTimeStyle }}>
                    <span style={dateStyle}>{call.created_time ? utcDateTimeExpressedInLocalTimeZone(call.created_time, 'medium') : EMPTY}</span>
                    <span style={timeStyle}>{call.created_time ? utcDateTimeExpressedInLocalTimeZone(call.created_time, undefined, 'short') : EMPTY}</span>
                </div>
                <div style={columnStyle('7%')}>{directionBadge(call.direction)}</div>
                <span style={columnStyle('22%')}>{call.number_name ?? EMPTY}</span>
                <span style={columnStyle('18%')}>{call.user_full_name ?? EMPTY}</span>
                <span style={columnStyle('8%')}>{(call.in_call_duration ?? call.duration_seconds) != null ? formatDuration(call.in_call_duration ?? call.duration_seconds) : EMPTY}</span>
                <div style={columnStyle('10%')}>
                    <AudioPlayer fetchAudioUrl={fetchAudioUrl} testId="audio-player" />
                </div>
            </div>

            {isExpanded && (
                <div style={detailStyle}>
                    <div style={detailItemStyle}>
                        <span style={detailLabelStyle}>From</span>
                        <span style={detailValueStyle}>{call.from_number ?? EMPTY}</span>
                    </div>
                    <div style={detailItemStyle}>
                        <span style={detailLabelStyle}>Tags</span>
                        <span style={detailValueStyle}>{call.tags ?? EMPTY}</span>
                    </div>
                    <div style={detailItemStyle}>
                        <span style={detailLabelStyle}>To</span>
                        <span style={detailValueStyle}>{call.to_number ?? EMPTY}</span>
                    </div>
                    <div style={detailItemStyle}>
                        <span style={detailLabelStyle}>Notes</span>
                        <span style={{ ...detailValueStyle, whiteSpace: 'pre-line' }}>{call.rep_notes ?? EMPTY}</span>
                    </div>
                    <div style={detailItemStyle}>
                        <span style={detailLabelStyle}>Call ID</span>
                        <span style={{ ...detailValueStyle, fontFamily: 'monospace' }}>{call.aircall_id ?? EMPTY}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CallRow;
